import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, KeyRound, UserCheck, UserX, Lock, Search, Filter, X, Sparkles, Trash2,
  Users as UsersIcon, Briefcase, UserPlus,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Button } from '@/components/Button';
import { PasswordSetInput, isPasswordSetValid } from '@/components/PasswordSetInput';
import { formatUserName } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import { useRootLabel } from '@/stores/scope';

const REMARKS_MAX_BYTES = 400;
const remarksByteLength = (s: string): number => new TextEncoder().encode(s).length;

/** 16文字の強めなランダムパスワード生成 */
const generatePassword = (): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // I, O 除外
  const lower = 'abcdefghjkmnpqrstuvwxyz';   // i, l, o 除外
  const digit = '23456789';                  // 0, 1 除外
  const symbol = '@#$%&*+-=?';
  const all = upper + lower + digit + symbol;
  const rand = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
  // 各カテゴリから最低1文字ずつ + 残り12文字ランダム
  const seed = [rand(upper), rand(lower), rand(digit), rand(symbol)];
  for (let i = 0; i < 12; i++) seed.push(rand(all));
  // シャッフル
  for (let i = seed.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [seed[i], seed[j]] = [seed[j]!, seed[i]!];
  }
  return seed.join('');
};

type UserRow = {
  user_id: string;
  email: string | null;           // v0.2 で任意化
  login_id: string;               // v0.2 で必須化
  name: string;
  display_name: string;
  family_name: string | null;
  given_name: string | null;
  middle_name: string | null;
  remarks: string | null;
  status: 'active' | 'suspended' | 'locked';
  last_login_at: string | null;
  created_at: string;
  // v0.3 (docs/design/v03-user-employee-refinement.md §2-1): ユーザー由来識別
  origin: 'employee' | 'external' | 'direct';
  linked_person_name: string | null;
  // employees:view scope内 (=isCompanyAdmin) の場合のみ含まれる (Sec H-3、それ以外はundefined)
  employee_id?: string | null;
  external_person_id?: string | null;
  // v0.3 (S-14): 単数role→複数roles (システムロール+カスタムロール併用対応)
  roles: Array<{ role_id: string; role_name: string }>;
  stores: Array<{ store_id: string; store_name: string; is_primary: boolean; role_in_node: string | null }>;
};

type Role = {
  role_id: string;
  role_name: string;
  description: string | null;
  is_system_role: boolean;
};

type Store = { store_id: string; store_name: string };

type CreateForm = {
  email: string;
  login_id: string;
  display_name: string;
  family_name: string;
  given_name: string;
  middle_name: string;
  remarks: string;
  password: string;
  role_ids: string[];
  store_ids: string[];
  // v0.2.5 (S-7): /employees から「この社員のログインアカウントを作成する」導線で来た場合にセットされる
  // (design doc の登録フロー「社員登録→user発行→相互リンク」対応、EmployeesPage.tsx 参照)
  employee_id?: string;
};

type EditForm = {
  user_id: string;
  display_name?: string;
  family_name?: string;
  given_name?: string;
  middle_name?: string;
  remarks?: string;
  login_id?: string;
  email?: string;
  status?: 'active' | 'suspended' | 'locked';
  password?: string;
  password_confirmation?: string;
  role_ids?: string[];
  store_ids?: string[];
  // v0.3 (§2-6 UI-U-15対応): 主所属。store_ids本体はPATCH /users/:idで更新するため、
  // primary切替のみ別途PUT /users/:id/node-assignmentsで反映する (§S-2実装メモ参照)
  primary_node_id?: string | null;
};

// 2026-08-05 デザインレビューCRITICAL対応: 当初 `bg-misespark-orange/10 text-misespark-orange`
// (設計書指定通り) だったが実測コントラスト比 ≈2.6:1 でWCAG AA(4.5:1)未達だったため、
// 同系色トーンでコントラストが確保されている alert.* トークンに置換 (alert.textは他画面の
// 警告表示で既に実測確認済みの色)
const originBadge: Record<UserRow['origin'], { label: string; cls: string; icon: typeof UsersIcon }> = {
  employee: { label: '社員', cls: 'bg-misespark-navy/10 text-misespark-navy', icon: UsersIcon },
  external: { label: '外部', cls: 'bg-alert-bg text-alert-text', icon: Briefcase },
  direct: { label: '直接', cls: 'bg-surface-sunken text-text-sub', icon: UserPlus },
};

function OriginBadge({ origin }: { origin: UserRow['origin'] }) {
  const cfg = originBadge[origin];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs whitespace-nowrap ${cfg.cls}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

const roleLabelMap: Record<string, string> = {
  platform_admin: 'プラットフォーム管理者',
  company_admin: '会社管理者',
  manager: 'マネージャー',
};

const roleColorMap: Record<string, string> = {
  platform_admin: 'bg-brand-black text-white',
  company_admin: 'bg-brand-red text-white',
  manager: 'bg-brand-orange text-white',
};

/**
 * v0.5 AIメニュー利用状況ランキング (D-981) の `edit_user` deep-link で使う純粋関数群。
 * DOM 環境 (jsdom 等) は未導入のため (ImportsPage.test.ts と同じ事情)、コンポーネント本体の
 * useEffect からロジックを切り出してテスト可能にする。
 */

/** `startEdit(u)` と同一の11フィールド初期化 (design §5-3)。再実装せず本関数に一本化する。 */
export const buildEditFormFromUser = (u: UserRow): EditForm => ({
  user_id: u.user_id,
  display_name: u.display_name,
  family_name: u.family_name ?? '',
  given_name: u.given_name ?? '',
  middle_name: u.middle_name ?? '',
  remarks: u.remarks ?? '',
  login_id: u.login_id,
  email: u.email ?? '',
  status: u.status,
  role_ids: u.roles.map((r) => r.role_id),
  store_ids: u.stores.map((s) => s.store_id),
  // §8-198型 (2026-09-24実測、production 7名で発覚): 主所属が未設定のまま保存された既存データを
  // 開いた際、店舗が1件だけなら自動的に主所属として扱う (EmployeeAffiliationEditor.tsx の
  // 同型修正 d1464c3 を踏襲)。
  primary_node_id: u.stores.find((s) => s.is_primary)?.store_id
    ?? (u.stores.length === 1 ? u.stores[0]!.store_id : null),
});

/** `edit_user` が一覧に存在しない場合は何もしない (design §5-3)。 */
export const resolveEditTargetUser = (users: UserRow[], editUserId: string | null): UserRow | undefined => {
  if (!editUserId) return undefined;
  return users.find((u) => u.user_id === editUserId);
};

export function UsersPage() {
  const rootLabel = useRootLabel();
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: () => apiFetch<UserRow[]>('/users'),
  });
  // v0.4 章① (§1-4 「self が無音で効かなくなる」検知): self scope のロールを持つのに
  // user_person_links が無い user の一覧。all_stores 保有者以外には 403 が返るため
  // (診断用途、§1-6) retry せず静かに空扱いにする
  const { data: missingLinkUsers = [] } = useQuery<UserRow[]>({
    queryKey: ['users', 'missing-person-link'],
    queryFn: () => apiFetch<UserRow[]>('/users?missing_person_link=1'),
    retry: false,
    throwOnError: false,
  });
  const missingLinkUserIds = useMemo(() => new Set(missingLinkUsers.map((u) => u.user_id)), [missingLinkUsers]);
  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => apiFetch<Role[]>('/roles'),
  });
  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  // v0.3 (§2-6 UI-U-16): 連動モード表示用。company_admin以外は403で取得できないことがあるが、
  // その場合は警告を出さないだけで良い (非致命的、retryしない)
  const { data: syncMode } = useQuery<{ mode: 'off' | 'overwrite' | 'append'; synced_user_count: number }>({
    queryKey: ['tenant-features', 'sync-user-org'],
    queryFn: () => apiFetch('/tenant-features/sync-user-org'),
    retry: false,
  });

  const currentUserId = useAuthStore((s) => s.user?.user_id);
  const [creating, setCreating] = useState<CreateForm | null>(null);
  const [editing, setEditing] = useState<EditForm | null>(null);
  const [deleting, setDeleting] = useState<UserRow | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | UserRow['status']>('all');

  // v0.3 (Design H-4 a11y、2026-08-05テストレビューMAJOR対応): 備考全文・所属+Nの表示を
  // title属性 (§2-2で明示的に禁止済みのXSS/改行崩れリスクパターン) からキーボード到達可能な
  // ポップオーバーに変更。開いているポップオーバーを`${種別}:${user_id}`のキーで1つだけ管理する
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  useEffect(() => {
    if (!openPopoverId) return;
    const closeOnOutsideOrEscape = (e: MouseEvent | KeyboardEvent) => {
      if (e.type === 'keydown' && (e as KeyboardEvent).key === 'Escape') { setOpenPopoverId(null); return; }
      if (e.type === 'mousedown' && !(e.target as HTMLElement).closest('[data-popover-root]')) setOpenPopoverId(null);
    };
    document.addEventListener('mousedown', closeOnOutsideOrEscape);
    document.addEventListener('keydown', closeOnOutsideOrEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideOrEscape);
      document.removeEventListener('keydown', closeOnOutsideOrEscape);
    };
  }, [openPopoverId]);

  // v0.2.5 (S-7): /employees の「この社員のログインアカウントを作成する」リンクから来た場合、
  // ?create_for_employee=<employee_id>&employee_name=<氏名> を読み取って新規作成モーダルを
  // 自動で開き、employee_id を紐付ける。消費後はURLから消して再読込時の再オープンを防ぐ。
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const employeeId = searchParams.get('create_for_employee');
    if (!employeeId) return;
    const employeeName = searchParams.get('employee_name') ?? '';
    const employeeFamilyName = searchParams.get('employee_family_name') ?? '';
    const employeeGivenName = searchParams.get('employee_given_name') ?? '';
    const employeeMiddleName = searchParams.get('employee_middle_name') ?? '';
    const managerRoleId = roles.find((r) => r.role_name === 'manager')?.role_id;
    setCreating({
      email: '', login_id: '',
      display_name: employeeName, family_name: employeeFamilyName, given_name: employeeGivenName, middle_name: employeeMiddleName,
      remarks: '', password: '',
      role_ids: managerRoleId ? [managerRoleId] : [],
      store_ids: [], employee_id: employeeId,
    });
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // v0.5 AIメニュー利用状況ランキング (D-981): ランキング画面の各行から
  // ?edit_user=<user_id> で遷移してきた場合、該当ユーザーの編集モーダルを自動で開く。
  // ⚠️ editing state を自前で組み立てず、既存の startEdit() をそのまま呼ぶこと
  // (design §5-3: startEdit は11フィールドを初期化しており、再実装すると所属店舗0件・
  //  email空欄のモーダルが開いてしまう)。edit_user 以外のクエリパラメータは保持する。
  useEffect(() => {
    const editUserId = searchParams.get('edit_user');
    if (!editUserId || !users) return;
    const target = resolveEditTargetUser(users, editUserId);
    if (target) startEdit(target);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('edit_user');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, users]);

  const [filterOrigin, setFilterOrigin] = useState<'all' | UserRow['origin']>('all');

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return users.filter((u) => {
      if (filterStatus !== 'all' && u.status !== filterStatus) return false;
      if (filterOrigin !== 'all' && u.origin !== filterOrigin) return false;
      if (q && !(
        (u.email ?? '').toLowerCase().includes(q) ||
        u.display_name.toLowerCase().includes(q) ||
        formatUserName(u).toLowerCase().includes(q) ||
        u.login_id.toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [users, searchText, filterStatus, filterOrigin]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<UserRow>(filtered as unknown as Record<string, unknown>[] & UserRow[], {
    defaultKey: 'name',
    defaultDir: 'asc',
    accessors: {
      name: (u: UserRow) => u.display_name,
      login_id: (u: UserRow) => u.login_id,
      email: (u: UserRow) => u.email ?? '',
      role: (u: UserRow) => u.roles.map((r) => r.role_name).join(','),
      status: (u: UserRow) => u.status,
      last_login_at: (u: UserRow) => u.last_login_at ?? '',
      created_at: (u: UserRow) => u.created_at,
    } as unknown as Record<string, (u: UserRow) => unknown>,
  });

  const hasAnyFilter = Boolean(searchText || filterStatus !== 'all' || filterOrigin !== 'all');
  const clearFilters = () => { setSearchText(''); setFilterStatus('all'); setFilterOrigin('all'); };

  const roleLabel = (r: Role | { role_name: string }): string =>
    roleLabelMap[r.role_name] ?? r.role_name;

  // 空文字を null に正規化してからサーバに送る（email は任意、空 = 未設定）
  const normalizeCreate = (f: CreateForm) => ({
    ...f,
    email: f.email.trim() === '' ? null : f.email.trim(),
    login_id: f.login_id.trim(),
    display_name: f.display_name.trim(),
    family_name: f.family_name.trim() || null,
    given_name: f.given_name.trim() || null,
    middle_name: f.middle_name.trim() || null,
    remarks: f.remarks.trim() || null,
  });
  const normalizeEdit = (f: EditForm) => ({
    ...f,
    // email が undefined（未変更）はそのまま、空文字は null（クリア意図）に
    email: f.email === undefined ? undefined : (f.email.trim() === '' ? null : f.email.trim()),
    login_id: f.login_id !== undefined ? f.login_id.trim() : undefined,
  });

  const createMut = useMutation({
    mutationFn: (f: CreateForm) => apiFetch('/users', {
      method: 'POST', body: JSON.stringify(normalizeCreate(f)),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreating(null);
      toast.success('ユーザーを追加しました');
    },
  });

  const updateMut = useMutation({
    mutationFn: async (f: EditForm) => {
      const { primary_node_id, ...patchFields } = normalizeEdit(f);
      await apiFetch(`/users/${f.user_id}`, { method: 'PATCH', body: JSON.stringify(patchFields) });
      // v0.3 (§2-6 UI-U-15): 主所属切替はstore_ids本体とは別のPUT /node-assignmentsで反映する
      if (f.store_ids !== undefined) {
        await apiFetch(`/users/${f.user_id}/node-assignments`, {
          method: 'PUT', body: JSON.stringify({ node_ids: f.store_ids, primary_node_id: primary_node_id ?? null }),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditing(null);
      toast.success('ユーザー情報を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => apiFetch(`/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      const name = deleting ? formatUserName(deleting) : '';
      setDeleting(null);
      toast.success(`ユーザー「${name}」を削除しました`);
    },
    onError: (e) => {
      // 会社管理者0人ガード（409 LAST_COMPANY_ADMIN）や自己削除（400）はメッセージのみ表示
      if (e instanceof ApiClientError) {
        toast.error(e.message);
      } else {
        toast.error('削除に失敗しました');
      }
    },
  });

  const statusChip = (status: UserRow['status']) => {
    const map = {
      active: { label: '有効', cls: 'bg-green-100 text-green-800', icon: UserCheck },
      suspended: { label: '停止中', cls: 'bg-neutral-line text-neutral-sub', icon: UserX },
      locked: { label: 'ロック', cls: 'bg-warning-bg text-warning-text', icon: Lock },
    };
    const cfg = map[status];
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${cfg.cls}`}>
        <Icon size={12} /> {cfg.label}
      </span>
    );
  };

  // テナント内で割当可能なロール（platform_admin は除外）
  const assignableRoles = roles.filter((r) => r.role_name !== 'platform_admin');

  const startEdit = (u: UserRow) => {
    setEditing(buildEditFormFromUser(u));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1">ユーザー管理</h1>
          <p className="text-sm text-neutral-sub mt-1">
            氏名ごとにロール（権限）と担当店舗を割り当てます
          </p>
        </div>
        <button
          onClick={() => {
            const managerRoleId = assignableRoles.find((r) => r.role_name === 'manager')?.role_id;
            setCreating({
              email: '', login_id: '',
              display_name: '', family_name: '', given_name: '', middle_name: '', remarks: '',
              password: '',
              role_ids: managerRoleId ? [managerRoleId] : [],
              store_ids: [],
            });
          }}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover"
        >
          <Plus size={16} /> 新規ユーザー
        </button>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-neutral-sub" />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as 'all' | UserRow['status'])}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="all">全ての状態</option>
          <option value="active">有効のみ</option>
          <option value="suspended">停止中のみ</option>
          <option value="locked">ロック中のみ</option>
        </select>
        <select aria-label="由来で絞り込み" value={filterOrigin} onChange={(e) => setFilterOrigin(e.target.value as 'all' | UserRow['origin'])}
          className="px-2 py-1 border border-neutral-line rounded text-sm">
          <option value="all">すべての由来</option>
          <option value="employee">社員</option>
          <option value="external">外部</option>
          <option value="direct">直接</option>
        </select>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" />
          <input type="text" value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="氏名・ログインID・メールで検索"
            className="pl-6 pr-2 py-1 border border-neutral-line rounded text-sm w-52" />
        </div>
        <div className="flex-1" />
        <div className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
          {sortedRows.length} 件表示中（全 {users.length} 件）
        </div>
        {hasAnyFilter && (
          <button type="button" onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-neutral-sub hover:text-brand-red">
            <X size={12} /> 絞込みをクリア
          </button>
        )}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="hidden md:block border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm table-fixed">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <SortableTh sortKey="name" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-36">氏名</SortableTh>
                <th scope="col" className="text-left px-4 py-3 w-20">由来</th>
                <SortableTh
                  sortKey="login_id"
                  currentKey={sortKey}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="w-36"
                  title="並び替えはログインIDで実行。下段はメールアドレスを参考表示"
                >
                  ログインID
                </SortableTh>
                <SortableTh sortKey="role" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-32">ロール</SortableTh>
                <th scope="col" className="text-left px-4 py-3 w-36">所属店舗</th>
                <th scope="col" className="text-left px-4 py-3 w-32">備考</th>
                <SortableTh sortKey="status" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-20">状態</SortableTh>
                <SortableTh sortKey="last_login_at" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-32">最終ログイン</SortableTh>
                <th scope="col" className="w-20 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((u) => {
                const primaryStore = u.stores.find((s) => s.is_primary);
                const otherStoresCount = u.stores.length - (primaryStore ? 1 : 0);
                return (
                <tr key={u.user_id} className="border-t border-neutral-line hover:bg-neutral-panel">
                  <td className="px-4 py-3 font-medium max-w-[16ch] truncate" title={formatUserName(u)}>
                    {formatUserName(u)}
                    {u.display_name !== formatUserName(u) && (
                      <div className="text-xs text-neutral-sub font-normal truncate">{u.display_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 flex items-center gap-1 flex-wrap">
                    <OriginBadge origin={u.origin} />
                    {missingLinkUserIds.has(u.user_id) && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap bg-alert-bg text-alert-text"
                        title="本人のみ閲覧可能な機能 (シフト・給与等) の権限を持っていますが、社員/外部人材とのリンクが未設定のため利用できません"
                      >
                        リンク未設定
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums overflow-hidden">
                    <div className="font-mono text-neutral-text text-sm truncate" title={u.login_id}>{u.login_id}</div>
                    {/* email 未設定時もダッシュを表示して行高を揃える */}
                    <div className="text-neutral-sub text-xs truncate" title={u.email ?? undefined}>{u.email || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    {u.roles.length === 0 ? (
                      <span className="text-neutral-sub text-xs">未割当</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <span key={r.role_id} className={`inline-block px-2 py-0.5 rounded text-xs ${roleColorMap[r.role_name] ?? 'bg-neutral-line text-neutral-text'}`}>
                            {roleLabel(r)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 overflow-hidden relative" data-popover-root>
                    {u.stores.length === 0 ? (
                      <span className="text-neutral-sub text-xs">−（{rootLabel}スコープ）</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 items-center">
                        {primaryStore && (
                          <span className="inline-block max-w-full truncate px-1.5 py-0.5 rounded bg-brand-red/10 text-brand-red text-xs font-medium">
                            {primaryStore.store_name}
                          </span>
                        )}
                        {otherStoresCount > 0 && (
                          <button type="button"
                            aria-expanded={openPopoverId === `stores:${u.user_id}`}
                            aria-controls={`stores-popover-${u.user_id}`}
                            onClick={() => setOpenPopoverId(openPopoverId === `stores:${u.user_id}` ? null : `stores:${u.user_id}`)}
                            className="inline-block px-1.5 py-0.5 rounded bg-neutral-line text-neutral-text text-xs hover:bg-neutral-line/70 focus-ring"
                          >
                            +{otherStoresCount}
                          </button>
                        )}
                        {!primaryStore && u.stores.map((s) => (
                          <span key={s.store_id} className="inline-block px-1.5 py-0.5 rounded bg-neutral-line text-neutral-text text-xs">
                            {s.store_name}
                          </span>
                        ))}
                      </div>
                    )}
                    {openPopoverId === `stores:${u.user_id}` && (
                      <div id={`stores-popover-${u.user_id}`} role="dialog" aria-label="所属店舗一覧"
                        className="absolute z-20 top-full left-0 mt-1 w-56 max-h-64 overflow-y-auto break-all p-2 rounded border border-neutral-line bg-white shadow-lg text-xs space-y-1">
                        {u.stores.map((s) => (
                          <div key={s.store_id}>{s.store_name}{s.is_primary ? ' (主所属)' : ''}</div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-sub overflow-hidden relative" data-popover-root>
                    <button type="button"
                      aria-expanded={openPopoverId === `remarks:${u.user_id}`}
                      aria-controls={`remarks-popover-${u.user_id}`}
                      disabled={!u.remarks}
                      onClick={() => setOpenPopoverId(openPopoverId === `remarks:${u.user_id}` ? null : `remarks:${u.user_id}`)}
                      className="line-clamp-1 break-all text-left w-full hover:underline focus-ring disabled:hover:no-underline disabled:cursor-default"
                    >
                      {u.remarks || '—'}
                    </button>
                    {openPopoverId === `remarks:${u.user_id}` && u.remarks && (
                      <div id={`remarks-popover-${u.user_id}`} role="dialog" aria-label="備考全文"
                        className="absolute z-20 top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto break-all whitespace-pre-wrap p-2 rounded border border-neutral-line bg-white shadow-lg text-xs text-neutral-text">
                        {u.remarks}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{statusChip(u.status)}</td>
                  <td className="px-4 py-3 text-neutral-sub text-xs tabular-nums">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('ja-JP') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" aria-label="編集" onClick={() => startEdit(u)}
                      className="text-brand-red p-1 hover:text-brand-red-hover"
                      title="編集"
                    >
                      <Pencil size={14} />
                    </button>
                    <button type="button"
                      aria-label={u.user_id === currentUserId ? '自分自身は削除できません' : '削除'}
                      onClick={() => setDeleting(u)}
                      disabled={u.user_id === currentUserId}
                      className="ml-1 p-1 text-neutral-sub hover:text-warning-text disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.user_id === currentUserId ? '自分自身は削除できません' : '削除'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {sortedRows.length === 0 && (
            <EmptyState
              message={hasAnyFilter ? 'フィルタ条件に一致するユーザーがありません' : 'ユーザーが登録されていません'}
              description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「＋ 新規ユーザー」から追加してください'}
              actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
              onAction={hasAnyFilter ? clearFilters : undefined}
            />
          )}
        </div>
      )}

      {/* モバイルカードレイアウト (<md、design doc §5-1、2026-08-05 デザイン/テストレビューCRITICAL対応:
          未実装だったため新設。デスクトップのテーブルとデータソースは共通、表示だけ切替える) */}
      {!isLoading && (
        <div className="md:hidden space-y-2">
          {sortedRows.map((u) => {
            const primaryStore = u.stores.find((s) => s.is_primary);
            return (
              <div key={u.user_id} className="p-4 rounded-lg border border-neutral-line bg-white space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium truncate">{u.display_name}</span>
                  <span className="flex items-center gap-1 flex-wrap justify-end">
                    <OriginBadge origin={u.origin} />
                    {missingLinkUserIds.has(u.user_id) && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap bg-alert-bg text-alert-text"
                        title="本人のみ閲覧可能な機能 (シフト・給与等) の権限を持っていますが、社員/外部人材とのリンクが未設定のため利用できません"
                      >
                        リンク未設定
                      </span>
                    )}
                  </span>
                </div>
                <div className="text-sm text-neutral-sub truncate">
                  {formatUserName(u)}
                  {primaryStore && <span className="ml-2">{primaryStore.store_name}{u.stores.length > 1 ? ` +${u.stores.length - 1}` : ''}</span>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-neutral-sub line-clamp-1 flex-1">{u.remarks || '—'}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {statusChip(u.status)}
                    <button type="button" aria-label="編集" onClick={() => startEdit(u)}
                      className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-sub hover:text-text-main"
                    >
                      <Pencil size={16} />
                    </button>
                    <button type="button"
                      aria-label={u.user_id === currentUserId ? '自分自身は削除できません' : '削除'}
                      onClick={() => setDeleting(u)}
                      disabled={u.user_id === currentUserId}
                      className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-sub hover:text-danger-text disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {sortedRows.length === 0 && (
            <EmptyState
              message={hasAnyFilter ? 'フィルタ条件に一致するユーザーがありません' : 'ユーザーが登録されていません'}
              description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「＋ 新規ユーザー」から追加してください'}
              actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
              onAction={hasAnyFilter ? clearFilters : undefined}
            />
          )}
        </div>
      )}

      {/* 新規作成モーダル (2026-08-05 デザインレビューCRITICAL対応: 生divから共通Modalコンポーネントへ
          移行、Escape閉じる・フォーカストラップ・aria-modal等のa11yを他モーダルと統一) */}
      <Modal
        open={creating !== null}
        title="新規ユーザー登録"
        onClose={() => setCreating(null)}
        footer={creating && (
          <>
            <Button variant="secondary" onClick={() => setCreating(null)}>キャンセル</Button>
            <Button variant="primary" type="submit" form="user-create-form"
              loading={createMut.isPending} loadingLabel="作成中…"
              disabled={creating.role_ids.length === 0 || !creating.display_name.trim() || remarksByteLength(creating.remarks) > REMARKS_MAX_BYTES}
            >
              作成
            </Button>
          </>
        )}
      >
        {creating && (
          <form id="user-create-form"
            onSubmit={(e) => { e.preventDefault(); createMut.mutate(creating); }}
            className="space-y-3"
          >
            {creating.employee_id && (
              <p className="text-xs text-neutral-sub bg-neutral-panel border border-neutral-line rounded p-2">
                社員「{creating.display_name || '（社員名未取得）'}」にこのログインアカウントを紐づけて作成します
              </p>
            )}

            <div>
              <label className="block text-sm text-neutral-sub mb-1">表示名*</label>
              <input required maxLength={100} value={creating.display_name}
                onChange={(e) => setCreating({ ...creating, display_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              <p className="text-xs text-neutral-sub mt-1">役職名や組織名を入れる場合は姓/名は空でもOKです</p>
            </div>

            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">姓 (Family)</label>
                <input value={creating.family_name} maxLength={50}
                  onChange={(e) => setCreating({ ...creating, family_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">ミドル (任意)</label>
                <input value={creating.middle_name} maxLength={50}
                  onChange={(e) => setCreating({ ...creating, middle_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">名 (Given)</label>
                <input value={creating.given_name} maxLength={50}
                  onChange={(e) => setCreating({ ...creating, given_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="creating-remarks" className="block text-sm text-neutral-sub">備考（任意）</label>
                <span className={`text-xs tabular-nums ${remarksByteLength(creating.remarks) > REMARKS_MAX_BYTES ? 'text-danger-text font-medium' : 'text-neutral-sub'}`}>
                  {remarksByteLength(creating.remarks)} / {REMARKS_MAX_BYTES} byte
                </span>
              </div>
              <textarea id="creating-remarks" rows={3} value={creating.remarks}
                onChange={(e) => setCreating({ ...creating, remarks: e.target.value })}
                className={`w-full px-3 py-2 rounded border focus:outline-none ${remarksByteLength(creating.remarks) > REMARKS_MAX_BYTES ? 'border-danger-text' : 'border-neutral-line focus-ring'}`} />
              {remarksByteLength(creating.remarks) > REMARKS_MAX_BYTES && (
                <p role="alert" aria-live="polite" className="text-xs text-danger-text mt-1">備考は400バイト（UTF-8）以内で入力してください</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">ログインID*</label>
                <input required type="text" minLength={1} maxLength={50} value={creating.login_id}
                  onChange={(e) => setCreating({ ...creating, login_id: e.target.value })}
                  placeholder="例：yamada2026"
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring font-mono" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">メールアドレス（任意）</label>
                <input type="email" value={creating.email}
                  onChange={(e) => setCreating({ ...creating, email: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>
            <p className="text-xs text-neutral-sub -mt-2">ログインIDは必須・全体で一意（50文字以内、半角推奨）。メールアドレスを設定するとどちらでもログインできます</p>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-neutral-sub">初期パスワード*（8文字以上）</label>
                <button type="button"
                  onClick={() => {
                    const pw = generatePassword();
                    setCreating({ ...creating, password: pw });
                    // 自動でクリップボードにもコピー（対応ブラウザのみ）
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(pw).then(
                        () => toast.info('パスワードを生成しクリップボードにコピーしました'),
                        () => toast.info('パスワードを生成しました'),
                      );
                    } else {
                      toast.info('パスワードを生成しました');
                    }
                  }}
                  className="flex items-center gap-1 text-xs text-brand-red hover:underline"
                >
                  <Sparkles size={12} /> 自動生成
                </button>
              </div>
              <input required type="text" minLength={8} value={creating.password}
                onChange={(e) => setCreating({ ...creating, password: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring font-mono" />
              <p className="text-xs text-neutral-sub mt-1">ユーザーに口頭またはメール等で別途連絡してください（画面共有時は「自動生成」後にすぐコピーして閉じるのが安全です）</p>
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">ロール* (複数選択可)</label>
              <div className="border border-neutral-line rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {assignableRoles.map((r) => (
                  <label key={r.role_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                      checked={creating.role_ids.includes(r.role_id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...creating.role_ids, r.role_id]
                          : creating.role_ids.filter((x) => x !== r.role_id);
                        setCreating({ ...creating, role_ids: next });
                      }}
                    />
                    {roleLabel(r)}
                  </label>
                ))}
              </div>
              {creating.role_ids.length === 0 && (
                <p className="text-xs text-warning-text mt-1">少なくとも1つのロールを選択してください</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">担当店舗（複数選択可）</label>
              <div className="border border-neutral-line rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {stores.map((s) => (
                  <label key={s.store_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                      checked={creating.store_ids.includes(s.store_id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...creating.store_ids, s.store_id]
                          : creating.store_ids.filter((x) => x !== s.store_id);
                        setCreating({ ...creating, store_ids: next });
                      }}
                    />
                    {s.store_name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-neutral-sub mt-1">
                会社管理者は自テナント全店の権限を持つため、担当店舗未指定でも全店データが見えます
              </p>
            </div>
          </form>
        )}
      </Modal>

      {/* 編集モーダル (2026-08-05 デザインレビューCRITICAL対応: Modal コンポーネントへ移行) */}
      {editing && (() => {
        const editingRow = users.find((u) => u.user_id === editing.user_id);
        return (
        <Modal
          open
          title="ユーザー編集"
          onClose={() => setEditing(null)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setEditing(null)}>キャンセル</Button>
              <Button variant="primary" type="submit" form="user-edit-form"
                loading={updateMut.isPending} loadingLabel="保存中…"
                disabled={
                  (editing.role_ids?.length ?? 0) === 0
                  || !(editing.display_name ?? '').trim()
                  || remarksByteLength(editing.remarks ?? '') > REMARKS_MAX_BYTES
                  || Boolean((editing.password || editing.password_confirmation) && !isPasswordSetValid(editing.password ?? '', editing.password_confirmation ?? ''))
                  // §8-198型: 担当店舗が1件以上あるのに主所属が未選択のまま保存させない
                  // (複数所属で選び忘れたケース。1件のみの場合は上のonChange/読込時に自動選択されるため到達しない)
                  || Boolean((editing.store_ids?.length ?? 0) > 0 && !editing.primary_node_id)
                }
              >
                保存
              </Button>
            </>
          )}
        >
          <form id="user-edit-form"
            onSubmit={(e) => { e.preventDefault(); updateMut.mutate(editing); }}
            className="space-y-3">
            {editingRow && (
              <div className="flex items-center gap-2">
                <OriginBadge origin={editingRow.origin} />
              </div>
            )}

            {editingRow && editingRow.origin !== 'direct' && (
              <p className="text-xs text-neutral-sub bg-neutral-panel border border-neutral-line rounded p-2">
                連携中の{editingRow.origin === 'employee' ? '社員' : '外部人材'}：{' '}
                {editingRow.employee_id ? (
                  <Link to={`/employees/${editingRow.employee_id}`} className="text-chrome-base hover:underline">{editingRow.linked_person_name}</Link>
                ) : (
                  <span>{editingRow.linked_person_name}</span>
                )}
              </p>
            )}

            <div>
              <label className="block text-sm text-neutral-sub mb-1">表示名*</label>
              <input required maxLength={100} value={editing.display_name ?? ''}
                onChange={(e) => setEditing({ ...editing, display_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <div>
                <label className="block text-sm text-neutral-sub mb-1">姓 (Family)</label>
                <input value={editing.family_name ?? ''} maxLength={50}
                  onChange={(e) => setEditing({ ...editing, family_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">ミドル (任意)</label>
                <input value={editing.middle_name ?? ''} maxLength={50}
                  onChange={(e) => setEditing({ ...editing, middle_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label className="block text-sm text-neutral-sub mb-1">名 (Given)</label>
                <input value={editing.given_name ?? ''} maxLength={50}
                  onChange={(e) => setEditing({ ...editing, given_name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="editing-remarks" className="block text-sm text-neutral-sub">備考（任意）</label>
                <span className={`text-xs tabular-nums ${remarksByteLength(editing.remarks ?? '') > REMARKS_MAX_BYTES ? 'text-danger-text font-medium' : 'text-neutral-sub'}`}>
                  {remarksByteLength(editing.remarks ?? '')} / {REMARKS_MAX_BYTES} byte
                </span>
              </div>
              <textarea id="editing-remarks" rows={3} value={editing.remarks ?? ''}
                onChange={(e) => setEditing({ ...editing, remarks: e.target.value })}
                className={`w-full px-3 py-2 rounded border focus:outline-none ${remarksByteLength(editing.remarks ?? '') > REMARKS_MAX_BYTES ? 'border-danger-text' : 'border-neutral-line focus-ring'}`} />
              {remarksByteLength(editing.remarks ?? '') > REMARKS_MAX_BYTES && (
                <p role="alert" aria-live="polite" className="text-xs text-danger-text mt-1">備考は400バイト（UTF-8）以内で入力してください</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">ログインID*</label>
              <input required type="text" minLength={1} maxLength={50} value={editing.login_id ?? ''}
                onChange={(e) => setEditing({ ...editing, login_id: e.target.value })}
                placeholder="例：yamada2026"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring font-mono" />
              <p className="text-xs text-neutral-sub mt-1">ログインIDは必須・全体で一意。空にすることはできません</p>
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">メールアドレス（任意）</label>
              <input type="email" value={editing.email ?? ''}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                placeholder="設定するとメールでもログインできます"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">ロール (複数選択可)</label>
              <div className="border border-neutral-line rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {assignableRoles.map((r) => (
                  <label key={r.role_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                      checked={editing.role_ids?.includes(r.role_id) ?? false}
                      onChange={(e) => {
                        const current = editing.role_ids ?? [];
                        const next = e.target.checked
                          ? [...current, r.role_id]
                          : current.filter((x) => x !== r.role_id);
                        setEditing({ ...editing, role_ids: next });
                      }}
                    />
                    {roleLabel(r)}
                  </label>
                ))}
              </div>
              {(editing.role_ids?.length ?? 0) === 0 && (
                <p className="text-xs text-warning-text mt-1">少なくとも1つのロールを選択してください</p>
              )}
            </div>

            {editingRow && editingRow.origin !== 'direct' && syncMode && syncMode.mode !== 'off' && (
              <p className="text-xs text-alert-text bg-alert-bg border border-alert-border/40 rounded p-2">
                連携中の{editingRow.origin === 'employee' ? '社員' : '外部人材'}の所属変更が自動反映される設定です
                （{syncMode.mode === 'overwrite' ? '上書き連動' : '追加連動'}）。ここで手動編集しても、
                次回の社員/外部人材側の所属変更で上書きされる場合があります。
              </p>
            )}
            <div>
              <label className="block text-sm text-neutral-sub mb-1">担当店舗（複数選択可）</label>
              <div className="border border-neutral-line rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {stores.map((s) => (
                  <label key={s.store_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                      checked={editing.store_ids?.includes(s.store_id) ?? false}
                      onChange={(e) => {
                        const current = editing.store_ids ?? [];
                        const next = e.target.checked
                          ? [...current, s.store_id]
                          : current.filter((x) => x !== s.store_id);
                        const uncheckedPrimary = !e.target.checked && editing.primary_node_id === s.store_id
                          ? null
                          : editing.primary_node_id;
                        // §8-198型: 選択結果が1件だけになったらラジオボタンを押さなくても主所属にする
                        // (EmployeeAffiliationEditor.tsx の handleSelectionChange と同型の自動防止)
                        const nextPrimary = next.length === 1 ? next[0]! : uncheckedPrimary;
                        setEditing({ ...editing, store_ids: next, primary_node_id: nextPrimary });
                      }}
                    />
                    {s.store_name}
                  </label>
                ))}
              </div>
              {(editing.store_ids?.length ?? 0) > 0 && (
                <fieldset className="mt-2">
                  <legend className="block text-xs text-neutral-sub mb-1">主所属</legend>
                  <div className="space-y-1">
                    {stores.filter((s) => editing.store_ids?.includes(s.store_id)).map((s) => (
                      <label key={s.store_id} className="flex items-center gap-2 text-sm">
                        <input type="radio" name="user-primary-node"
                          checked={editing.primary_node_id === s.store_id}
                          onChange={() => setEditing({ ...editing, primary_node_id: s.store_id })}
                        />
                        {s.store_name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              {(editing.store_ids?.length ?? 0) > 0 && !editing.primary_node_id && (
                <p className="text-xs text-warning-text mt-1">主所属を1つ選択してください</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">状態</label>
              <select value={editing.status ?? 'active'}
                onChange={(e) => setEditing({ ...editing, status: e.target.value as UserRow['status'] })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
                <option value="active">有効</option>
                <option value="suspended">停止</option>
                {/* ⚠️ 「ロック」は手動で選べない (API の zod enum からも外してある)。
                    locked は「パスワード連続失敗によるシステム起因のロック」専用にして意味を1つに固定した。
                    既にロック中の人は状態が見えないと解除できないため、その場合だけ選択不可の項目として出す */}
                {editing.status === 'locked' && (
                  <option value="locked" disabled>ロック中（パスワード連続失敗）</option>
                )}
              </select>
              <p className="text-xs text-neutral-sub mt-1">
                ロック解除は「有効」に変更（失敗回数もリセット）。利用を止めるときは「停止」を使います
              </p>
            </div>

            <div>
              <label className="block text-sm text-neutral-sub mb-1">
                <KeyRound size={12} className="inline mr-1" />
                パスワードリセット（新パスワードを入力する場合のみ）
              </label>
              <PasswordSetInput
                idPrefix="user-edit"
                value={editing.password ?? ''}
                confirmValue={editing.password_confirmation ?? ''}
                onChange={(v) => setEditing({ ...editing, password: v || undefined })}
                onConfirmChange={(v) => setEditing({ ...editing, password_confirmation: v || undefined })}
                label="新しいパスワード"
                confirmLabel="確認用"
              />
            </div>
          </form>
        </Modal>
        );
      })()}

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleting !== null}
        title="ユーザーを削除しますか？"
        destructive
        confirmLabel="削除する"
        cancelLabel="キャンセル"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.user_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>
              以下のユーザーを完全に削除します。この操作は<strong className="text-danger-text">取り消せません</strong>。
            </p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">氏名：</span><span className="font-medium">{formatUserName(deleting)}</span></div>
              <div><span className="text-neutral-sub">ログインID：</span><span className="font-mono">{deleting.login_id}</span></div>
              {deleting.email && (
                <div><span className="text-neutral-sub">メール：</span><span className="tabular-nums">{deleting.email}</span></div>
              )}
              {deleting.roles.length > 0 && (
                <div><span className="text-neutral-sub">ロール：</span>{deleting.roles.map((r) => roleLabel(r)).join('、')}</div>
              )}
              <div><span className="text-neutral-sub">状態：</span>{statusChip(deleting.status)}</div>
              {deleting.stores.length > 0 && (
                <div>
                  <span className="text-neutral-sub">担当店舗：</span>
                  {deleting.stores.map((s) => s.store_name).join('、')}
                </div>
              )}
            </div>
            {deleting.roles.some((r) => r.role_name === 'company_admin') && (
              <p className="text-xs text-warning-text">
                ※ 会社管理者を削除しようとしています。削除後に会社管理者が0人になる場合は、サーバー側でブロックされます。
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
}
